
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "./use-toast";

export interface MFOCode {
  id: string;
  code: string;
  title: string;
  budget: number;
  created_at?: string;
  updated_at?: string;
  owner_id?: string;
}

export const useMFOCodes = () => {
  const [mfoCodes, setMfoCodes] = useState<MFOCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchMFOCodes = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      
      const { data, error } = await supabase
        .from('mfo_codes' as any)
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) {
        throw error;
      }
      

      setMfoCodes((data as unknown) as MFOCode[]);
      return data;
    } catch (error: any) {
      console.error("Error fetching MFO codes:", error);
      setError(error.message);
      toast({
        title: "Failed to load MFO codes",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchMFOCodes();
    
    // Set up a refresh interval
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {

        fetchMFOCodes();
      }
    }, 60 * 1000); // Refresh every minute if tab is visible
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [fetchMFOCodes]);

  const createMFOCode = async (code: Omit<MFOCode, 'id'>) => {
    try {
      const { data, error } = await supabase
        .from('mfo_codes' as any)
        .insert({
          code: code.code,
          title: code.title,
          budget: code.budget,
          owner_id: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();
        
      if (error) {
        throw error;
      }
      
      // Convert to unknown first before casting to MFOCode
      setMfoCodes(prev => [(data as unknown) as MFOCode, ...prev]);
      
      toast({
        title: "MFO Code Created",
        description: `Successfully created ${code.code}: ${code.title}`,
      });
      
      return data;
    } catch (error: any) {
      console.error("Error creating MFO code:", error);
      toast({
        title: "Failed to create MFO code",
        description: error.message,
        variant: "destructive"
      });
      throw error;
    }
  };

  const updateMFOCode = async (code: Partial<MFOCode>) => {
    if (!code.id) {
      throw new Error("MFO code ID is required for update");
    }
    
    try {
      const { data, error } = await supabase
        .from('mfo_codes' as any)
        .update({
          code: code.code,
          title: code.title,
          budget: code.budget,
          updated_at: new Date().toISOString()
        })
        .eq('id', code.id)
        .select()
        .single();
        
      if (error) {
        throw error;
      }
      
      // Update the local state with the updated MFO code
      setMfoCodes(prev => prev.map(prevCode => 
        prevCode.id === code.id ? { ...prevCode, ...code } : prevCode
      ));
      
      toast({
        title: "MFO Code Updated",
        description: `Successfully updated ${code.code}`,
      });
      
      return data;
    } catch (error: any) {
      console.error("Error updating MFO code:", error);
      toast({
        title: "Failed to update MFO code",
        description: error.message,
        variant: "destructive"
      });
      throw error;
    }
  };

  const deleteMFOCode = async (id: string) => {
    try {
      // Use any to bypass TypeScript's strict checking
      const { error } = await supabase
        .from('mfo_codes' as any)
        .delete()
        .eq('id', id);
        
      if (error) {
        throw error;
      }
      
      setMfoCodes(prev => prev.filter(code => code.id !== id));
      
      toast({
        title: "MFO code deleted",
        description: "The MFO code has been successfully deleted"
      });
      
      return true;
    } catch (error: any) {
      console.error("Error deleting MFO code:", error);
      toast({
        title: "Failed to delete MFO code",
        description: error.message,
        variant: "destructive"
      });
      throw error;
    }
  };

  return {
    mfoCodes,
    isLoading,
    error,
    fetchMFOCodes,
    createMFOCode,
    updateMFOCode,
    deleteMFOCode
  };
};
